import Component from '@glimmer/component';

/**
 * Controls semantic color and default weight; `inherit` adds no color class,
 * leaving ancestor and component recipes in control of the foreground.
 */
export type TextVariant = 'body' | 'muted' | 'label' | 'error' | 'success' | 'warning' | 'inherit';

export type TextSize = 'xs' | 'sm' | 'base' | 'lg';

export type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';

/** Shared body-copy roles that own size, weight, and line-height. */
export type TextRole = 'body' | 'body-secondary' | 'label' | 'caption';

export type TextTag = 'span' | 'p' | 'label' | 'div';

interface TextSignature {
  Args: {
    /** Semantic variant controlling color. Default: 'body' */
    variant?: TextVariant;
    /** Typography role. Defaults from `@variant`; explicit size and weight override it. */
    role?: TextRole;
    /** Compatibility font-size override applied over the typography role. */
    size?: TextSize;
    /** Compatibility font-weight override applied over the typography role. */
    weight?: TextWeight;
    /** HTML element to render. Default: 'span' */
    tag?: TextTag;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

const VARIANT_CLASSES: Record<TextVariant, string> = {
  body: 'tio-text--body',
  muted: 'tio-text--muted',
  label: 'tio-text--label',
  error: 'tio-text--error',
  success: 'tio-text--success',
  warning: 'tio-text--warning',
  inherit: '',
};

const SIZE_CLASSES: Record<TextSize, string> = {
  xs: 'tio-type-size-xs',
  sm: 'tio-type-size-sm',
  base: 'tio-type-size-base',
  lg: 'tio-type-size-lg',
};

const WEIGHT_CLASSES: Record<TextWeight, string> = {
  normal: 'tio-type-weight-normal',
  medium: 'tio-type-weight-medium',
  semibold: 'tio-type-weight-semibold',
  bold: 'tio-type-weight-bold',
};

const ROLE_CLASSES: Record<TextRole, string> = {
  body: 'tio-type-body',
  'body-secondary': 'tio-type-body-secondary',
  label: 'tio-type-label',
  caption: 'tio-type-caption',
};

const VARIANT_DEFAULT_ROLE: Record<TextVariant, TextRole> = {
  body: 'body',
  muted: 'body-secondary',
  label: 'label',
  error: 'body',
  success: 'body',
  warning: 'body',
  inherit: 'body',
};

/**
 * Text – a typography primitive for body/inline text.
 *
 * Renders a `<span>` (or other tag) with variant-driven color,
 * configurable size and weight.
 *
 * @example Basic
 * ```gts
 * import Text from 'tio-ui/components/text';
 *
 * <Text @variant="muted" @size="sm">Helper text</Text>
 * ```
 *
 * @example As paragraph with weight
 * ```gts
 * <Text @tag="p" @weight="semibold">Important note</Text>
 * ```
 */
export default class Text extends Component<TextSignature> {
  get variant() {
    return this.args.variant ?? 'body';
  }

  get tag() {
    return this.args.tag ?? 'span';
  }

  get variantClass() {
    return VARIANT_CLASSES[this.variant];
  }

  get roleClass() {
    return ROLE_CLASSES[this.args.role ?? VARIANT_DEFAULT_ROLE[this.variant]];
  }

  get sizeClass() {
    return this.args.size ? SIZE_CLASSES[this.args.size] : '';
  }

  get weightClass() {
    return this.args.weight ? WEIGHT_CLASSES[this.args.weight] : '';
  }

  get classes() {
    return [this.variantClass, this.roleClass, this.sizeClass, this.weightClass]
      .filter(Boolean)
      .join(' ');
  }

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag data-test-text class={{this.classes}} ...attributes>{{yield}}</Tag>
    {{/let}}
  </template>
}

export type { TextSignature };
